import axios from 'axios'
import { useNotificationStore } from '@/stores/notification'
import { resolveErrorMessage } from '@/http/errorMessages'

export const solbioApi = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASEURL}`,
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    Accept: 'application/json'
  }
})

export const csrfCookie = () => solbioApi.get('/sanctum/csrf-cookie')

export const getCompetitionState = () => solbioApi.get('/competitions/state')
export const getGeneralState = () => solbioApi.get('/competitions/state')
export const getRoleState = () => solbioApi.get('/competitions/role-state')

export const login = (credentials) => solbioApi.post(`/login`, credentials, { handleErrors: false })

export const logout = () => solbioApi.post(`/logout`, {})

/**
 * Password reset api calls
 */

export const passwordResetRequest = (data) => solbioApi.post('/passwordreset/request', data)

export const confirmUserToken = (data) => solbioApi.post('/passwordreset/confirm', data)

export const passwordResetStore = (data) => solbioApi.post('/passwordreset/store', data)

/**
 * User api calls
 */

export const getUser = () => solbioApi.get('/user/data')

export const isSessionActive = () => solbioApi.get('/user/sessionactive')

export const verifyEmailToken = (token) =>
  solbioApi.post('/user/verify-email-token', { token: token }, { handleErrors: false })

export const createUserWithPassword = (data) => solbioApi.post('/user/create', data)

// Druga część pary z verifyEmailToken — wołana, gdy tamta zwróci
// mode "email-change". Bez hasła: konto już istnieje, zmienia się tylko login.
// handleErrors: false, bo widok pokazuje własny komunikat o tokenie.
export const confirmEmailChange = (token) =>
  solbioApi.post('/user/update-email', { token: token }, { handleErrors: false })

export const sendTokenRequest = (data) => solbioApi.post('/user/new-token-request', data)

/**
 * School api calls
 */
export const getSchoolList = (params) =>
  solbioApi.get('/school/list', {
    params: {
      ...params
    }
  })

export const getSchoolListConfig = () => solbioApi.get('/school/list/config')

export const getSchoolsForDistrict = (districtId, pagenumber) =>
  solbioApi.get('/school/koob/' + districtId + '/list?page=' + pagenumber)

export const getSchoolData = (schoolId) => solbioApi.get('/school/' + schoolId + '/data')

export const saveSchoolRegistration = (registration) =>
  solbioApi.post('/school/register', registration, { handleErrors: false })

export const getSchoolById = (schoolId) => solbioApi.get('/school/' + schoolId + '/show')

export const saveSchool = (schoolId, data) =>
  solbioApi.post('/school/' + schoolId + '/update', data, { handleErrors: false })

// Kod awaryjny do pobrania testu (kgob, okno dystrybucji) — odpowiedź { code }.
export const generateBackupTestAccessCode = () =>
  solbioApi.post('/competitions/stage-school/backup-test-access-code', {})

// Pobranie testu z kodem dostępu: request = { code }.
export const DownloadTestFile = (schoolId, request) =>
  solbioApi.post(`/school/${schoolId}/test/download`, request, {
    responseType: 'blob'
  })

// Zasady oceniania etapu szkolnego — szkoła od stage.school.scoring-criteria.visible-from.
export const DownloadScoringCriteriaFile = (schoolId) =>
  solbioApi.post(
    `/school/${schoolId}/test/downloadtemplate`,
    {},
    {
      responseType: 'blob'
    }
  )

export const getStudentListForProtocol = (schoolId) =>
  solbioApi.get(`/school/${schoolId}/protocol/get-list`)

export const saveSchoolCompetitionProtocol = (schoolId, data) =>
  solbioApi.post(`/school/${schoolId}/protocol/save`, data)

export const getSchoolProtocol = (schoolId) => solbioApi.get(`/school/${schoolId}/protocol/show`)

// Protokół etapu szkolnego w PDF — tylko szkoła-właściciel, po złożeniu protokołu.
export const downloadSchoolProtocol = (schoolId) =>
  solbioApi.post(`/school/${schoolId}/protocol/download`, {}, { responseType: 'blob' })

/**
 * Student api calls
 */

export const createStudent = (schoolId, data) =>
  solbioApi.post(`/student/school/${schoolId}/create`, data, { handleErrors: false })

export const getStudentList = (params) => solbioApi.get('/student/list', { params })

// Konfiguracja listy uczniów: filtry i sortery dostępne dla roli w bieżącym etapie
// oraz domyślna kolejność. Osobne żądanie, bo ma inny cykl życia niż sama lista.
export const getStudentListConfig = () => solbioApi.get('/student/list/config')

export const getStudentData = (studentId) => solbioApi.get('/student/' + studentId + '/data')

export const setStudentDataReady = (studentId) =>
  solbioApi.post('/student/' + studentId + '/data-ready', {})

export const checkStudent = (data) => solbioApi.post('/student/check', data)

export const checkStudentEmail = (data) => solbioApi.post('/student/email-check', data)

export const updateStudent = (studentId, data) =>
  solbioApi.post('/student/' + studentId + '/update', data, { handleErrors: false })

export const confirmData = (studentId) => solbioApi.post(`/student/${studentId}/data-confirm`, {})

export const rejectData = (studentId) => solbioApi.post(`/student/${studentId}/data-reject`, {})

export const saveWorkTopicStatus = (studentId, data) =>
  solbioApi.post(`/student/${studentId}/set-work-topic-status`, data)

export const saveWorkFileStatus = (studentId, data) =>
  solbioApi.post(`/student/${studentId}/set-workfile-status`, data)

export const createNewWorkTopic = (studentId, data) =>
  solbioApi.post(`/student/${studentId}/create-work-topic`, data)

export const updateWorkTopic = (studentId, data) =>
  solbioApi.post(`/student/${studentId}/update-work-topic`, data)

export const DownloadWorkFile = (schoolId) =>
  solbioApi.get(`/student/${schoolId}/file-download`, {
    responseType: 'blob'
  })

export const getAttendanceList = () =>
  solbioApi.get('/student/attendance-list', {
    responseType: 'blob'
  })

export const getAnswerSheet = () =>
  solbioApi.get('/student/answer-sheet-download', {
    responseType: 'blob'
  })

export const getSchoolExamSurvey = () => solbioApi.get('/student/school-exam-survey')

export const saveSchoolExamSurvey = (content) =>
  solbioApi.post('/student/school-exam-survey', { content })

export const updateStudentDisciplines = (studentId, data) =>
  solbioApi.post(`/student/${studentId}/update-disciplines`, data)

export const updateStudentReviewers = (studentId, data) =>
  solbioApi.post(`/student/${studentId}/update-reviewers`, data)

export const updateStudentreview = (studentId, data) =>
  solbioApi.post(`/student/${studentId}/update-review`, data)

export const setReviewPending = (studentId) =>
  solbioApi.post(`/student/${studentId}/review/pending`, {})

export const setReviewReady = (studentId) =>
  solbioApi.post(`/student/${studentId}/review/ready`, {})

export const setReviewPublished = (studentId) =>
  solbioApi.post(`/student/${studentId}/review/published`, {})

// export const uploadFile = (studentId, data, onProgress) => {
//   return solbioApi.post(`/student/${studentId}/file-upload`, data, {
//     headers: {
//       'Content-Type': 'multipart/form-data'
//     },
//     onUploadProgress: (event) => {
//       if (!event.total) return
//       const percent = Math.round((event.loaded * 100) / event.total)
//       if (typeof onProgress === 'function') {
//         onProgress(percent)
//       }
//     }
//   })
// }

/**
 * Protocol API calls
 */

export const getProtocols = (params) => solbioApi.get('/protocol/list', { params })
export const getProtocolSummary = () => solbioApi.get('/protocol/summary')
export const getProtocolData = (id) => solbioApi.get(`/protocol/${id}/get`)
export const saveProtocolData = (id, data) => solbioApi.post(`/protocol/${id}/save`, data)

/**
 * Reviewer API calls
 */

export const getReviewertList = (params) => solbioApi.get('/reviewer/list', { params })
export const getReviewerData = (id) => solbioApi.get(`/reviewer/${id}/show`)
export const updateReviewer = (data) => solbioApi.post(`/reviewer/${data.id}/update`, data)
export const createReviewer = (data) => solbioApi.post('/reviewer/create', data)
export const getScoredReviewers = (data) => solbioApi.post('/reviewer/scored/list', data)

/**
 * Other api calls
 */

export const rspoFind = (id) => solbioApi.get(`/rspo/school/${id}`)

export const formDataDocuments = () => solbioApi.get('/form-data/documents')

export const formDataDegrees = () => solbioApi.get('/form-data/degrees')

export const formDataDistricts = () => solbioApi.get('/form-data/districts')

export const formDataDisciplines = () => solbioApi.get('/form-data/disciplines')

export const formDataReviewerRole = () => solbioApi.get('/form-data/reviewer-role')

export const stageDistrictSetTreshold = (data) =>
  solbioApi.post('/competitions/stage-district/set-threshold', data)

export const stageDistrictGetTreshold = () =>
  solbioApi.get('/competitions/stage-district/get-threshold')

export const stageDistrictCalculateResults = () =>
  solbioApi.get('/competitions/stage-district/calculate-results')

export const stageDistrictShowresults = () =>
  solbioApi.get('/competitions/stage-district/show-results')

export const enableShowReviews = () => solbioApi.get('/competitions/stage-central/show-reviews')

export const getCompetitionDates = () => solbioApi.get('/competitions/dates')

export const saveCompetitionDates = (data) => solbioApi.post('/competitions/dates', data)

/**
 * Error code handling
 *
 * Treść komunikatu wybiera resolveErrorMessage() — po kodzie z koperty
 * {"code": "..."}, a dla odpowiedzi bez koperty po statusie HTTP.
 */
/**
 * Przy responseType: 'blob' koperta błędu {"code": ...} przychodzi jako Blob —
 * bez odczytania notyfikacja nie rozpoznałaby kodu biznesowego.
 */
async function parseBlobErrorBody(error) {
  const data = error?.response?.data
  if (!(data instanceof Blob) || !data.type?.includes('json')) {
    return
  }
  try {
    error.response.data = JSON.parse(await data.text())
  } catch {
    // treść nie jest JSON-em — zostaje Blob, komunikat wg statusu HTTP
  }
}

solbioApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    await parseBlobErrorBody(error)

    if (error.config?.handleErrors === false) {
      return Promise.reject(error)
    }

    const notifications = useNotificationStore()

    notifications.addAlert({
      severity: 'error',
      ...resolveErrorMessage(error),
      life: 3000
    })

    return Promise.reject(error)
  }
)
